import React, { useCallback } from 'react';
import { FormField, INDICATION_TYPES, Input, InputTypes, Radio, RadioGroup } from 'taboola-ultimate-ui';
import { useEventValueHandler } from 'hooks';
import { FREQUENCY } from 'modules/campaigns/modules/common-campaign-form/config';
import { withIndication } from 'modules/errors';
import { COMMON_FLAGS, useCommonConfig } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useFormValidatedValue, validationFunctions } from 'modules/taboola-common-frontend-modules/validations';
import { FrequencyCappingDropDown } from './FrequencyCappingDropDown';
import { FREQUENCY_MODE } from './frequencyMode';
import styles from './UserFrequencyCapping.module.scss';

export const FREQUENCY_MODE_VALUES = [
    {
        value: FREQUENCY_MODE.NONE,
        title: (
            <FormattedMessage
                id="campaign.editor.UserFrequencyCapping.mode.none"
                defaultMessage="Let us optimize how often your ads are shown (recommended)"
            />
        ),
    },
    {
        value: FREQUENCY_MODE.CUSTOM,
        title: (
            <FormattedMessage
                id="video.campaign.editor.UserFrequencyCapping.mode.custom"
                defaultMessage="Set a preference"
            />
        ),
    },
];
const validations = [
    {
        validationFn: (value, _, { userImpressionsAmount, frequencyCappingMode }) =>
            isCustomMode(frequencyCappingMode) ? validationFunctions.isInt(userImpressionsAmount.toString()) : true,
        messageId: 'campaign.editor.UserFrequencyCapping.validations.error.whole.number',
        defaultMessage: 'User Frequency Capping must be a whole number',
        severity: INDICATION_TYPES.ERROR,
    },
    {
        validationFn: (value, _, { userImpressionsAmount, frequencyCappingMode }) =>
            isCustomMode(frequencyCappingMode) ? userImpressionsAmount >= 0 : true,
        messageId: 'campaign.editor.UserFrequencyCapping.validations.error.negative.number',
        defaultMessage: 'Enter specific user frequency targeting',
        severity: INDICATION_TYPES.ERROR,
    },
    {
        validationFn: (value, _, { userImpressionsAmount, frequencyValue, dayThreshold, frequencyCappingMode }) =>
            isCustomMode(frequencyCappingMode) && frequencyValue === FREQUENCY.DAY
                ? userImpressionsAmount >= dayThreshold
                : true,
        messageId: 'campaign.editor.UserFrequencyCapping.validations.warning.low.value',
        defaultMessage:
            'Your selected frequency cap may affect performance. Consider increasing or removing the cap for better results.',
        severity: INDICATION_TYPES.WARNING,
    },
    {
        validationFn: (value, _, { userImpressionsAmount, frequencyValue, weekThreshold, frequencyCappingMode }) =>
            isCustomMode(frequencyCappingMode) && frequencyValue === FREQUENCY.WEEK
                ? userImpressionsAmount >= weekThreshold
                : true,
        messageId: 'campaign.editor.UserFrequencyCapping.validations.warning.low.value',
        defaultMessage:
            'Your selected frequency cap may affect performance. Consider increasing or removing the cap for better results.',
        severity: INDICATION_TYPES.WARNING,
    },
    {
        validationFn: (value, _, { userImpressionsAmount, frequencyValue, monthThreshold, frequencyCappingMode }) =>
            isCustomMode(frequencyCappingMode) && frequencyValue === FREQUENCY.MONTH
                ? userImpressionsAmount >= monthThreshold
                : true,
        messageId: 'campaign.editor.UserFrequencyCapping.validations.warning.low.value',
        defaultMessage:
            'Your selected frequency cap may affect performance. Consider increasing or removing the cap for better results.',
        severity: INDICATION_TYPES.WARNING,
    },
];

const FormFieldWithIndication = withIndication(FormField);

const isCustomMode = value => value === FREQUENCY_MODE.CUSTOM;

export const UserFrequencyCapping = () => {
    const { value: frequencyValue, onChange: onFrequencyCappingChange } = useFormFieldValue({
        field: 'campaignTargeting.userFrequencyCapping.frequency',
    });
    const { value: frequencyCappingMode, onChange: onFrequencyCappingModeChange } = useFormFieldValue({
        field: 'campaignTargeting.userFrequencyCapping.mode',
    });

    const { value: userImpressionsAmount, onChange: setUserImpressionsAmount } = useFormFieldValue({
        field: 'campaignTargeting.userFrequencyCapping.userImpressionsAmount',
    });

    const { [COMMON_FLAGS.FREQUENCY_CAPPING_DAY_THRESHOLD]: dayThreshold } = useCommonConfig([
        COMMON_FLAGS.FREQUENCY_CAPPING_DAY_THRESHOLD,
    ]);

    const { [COMMON_FLAGS.FREQUENCY_CAPPING_WEEK_THRESHOLD]: weekThreshold } = useCommonConfig([
        COMMON_FLAGS.FREQUENCY_CAPPING_WEEK_THRESHOLD,
    ]);

    const { [COMMON_FLAGS.FREQUENCY_CAPPING_MONTH_THRESHOLD]: monthThreshold } = useCommonConfig([
        COMMON_FLAGS.FREQUENCY_CAPPING_MONTH_THRESHOLD,
    ]);

    const withFrequencyCappingMode = isCustomMode(frequencyCappingMode);

    const { indicationData, scrollRef } = useFormValidatedValue({
        field: 'campaignTargeting.userFrequencyCapping',
        validations,
        validationDependencies: {
            userImpressionsAmount,
            frequencyCappingMode,
            frequencyValue,
            dayThreshold,
            weekThreshold,
            monthThreshold,
        },
    });

    const handleChangeMode = useCallback(
        value => {
            if (value === FREQUENCY_MODE.CUSTOM) {
                setUserImpressionsAmount(dayThreshold);
                onFrequencyCappingChange(FREQUENCY.DAY);
            } else {
                setUserImpressionsAmount(null);
                onFrequencyCappingChange(FREQUENCY.NONE);
            }
            onFrequencyCappingModeChange(value);
        },
        [setUserImpressionsAmount, onFrequencyCappingChange, onFrequencyCappingModeChange, dayThreshold]
    );
    const handleUserImpressionsAmountChange = useEventValueHandler(setUserImpressionsAmount);
    return (
        <FormFieldWithIndication ref={scrollRef} {...indicationData}>
            <RadioGroup
                id="frequency-capping-mode"
                selectedValue={frequencyCappingMode}
                onChange={handleChangeMode}
                className={styles['radio-group']}
            >
                {FREQUENCY_MODE_VALUES.map(({ value, title }) => (
                    <Radio labelClassName={styles['radio-label']} key={value} value={value} title={title} />
                ))}
            </RadioGroup>
            {withFrequencyCappingMode && (
                <div>
                    <FormattedMessage
                        id="campaign.editor.UserFrequencyCapping.custom.description"
                        defaultMessage="Manage impressions for the entire campaign"
                    />
                    <div className={styles['inputs-container']}>
                        <Input
                            id="campaignTargeting.userFrequencyCapping.frequency"
                            type={InputTypes.NUMBER}
                            value={userImpressionsAmount}
                            onChange={handleUserImpressionsAmountChange}
                            data-metrics-event-name={GTM_EVENTS.USABILITY}
                            data-metrics-component="UserFrequencyCapping"
                            inputClass={styles['inline']}
                            mainClass={styles['input-wrapper']}
                        />
                        <span className={styles['text']}>per</span>
                        <FrequencyCappingDropDown />
                    </div>
                </div>
            )}
        </FormFieldWithIndication>
    );
};
